import { Alert, Text } from '@mantine/core';
import {
  createOperationScopeSnapshot,
  type AsyncOperationsSummary,
  useApiBaseUrl,
  useApiDataRegion,
} from '@vrplatform/data';
import { useCallback, useMemo } from 'react';
import {
  useRecoverOperations,
  useVisibleTrackedOperationForResource,
} from './operationTracker.hooks';

type Props = {
  connectionId: string;
  operations: AsyncOperationsSummary;
  tenantId: string;
};

const getActiveMessage = (type: string) => {
  switch (type) {
    case 'connection-archive':
      return 'Archiving connection...';
    case 'connection-connect':
      return 'Connected; finishing setup...';
    case 'flow-run':
      return 'Running workflow...';
    case 'connection-extract':
    case 'connection-sync':
      return 'Fetching the latest data...';
    default:
      return 'Updating connection...';
  }
};

export const ConnectionOperationStatus = ({
  connectionId,
  operations,
  tenantId,
}: Props) => {
  const dataRegion = useApiDataRegion();
  const regionalApiBaseUrl = useApiBaseUrl();
  const scope = useMemo(
    () =>
      dataRegion && regionalApiBaseUrl
        ? createOperationScopeSnapshot({
            apiTarget: 'regional',
            dataRegion,
            regionalApiBaseUrl,
            requesterTeamId: tenantId,
          })
        : null,
    [dataRegion, regionalApiBaseUrl, tenantId]
  );
  const resolveScope = useCallback(() => scope, [scope]);

  useRecoverOperations({
    summary: operations,
    resolveScope,
  });

  if (!scope) return null;

  return (
    <ScopedConnectionOperationStatus
      connectionId={connectionId}
      scope={scope}
    />
  );
};

const ScopedConnectionOperationStatus = ({
  connectionId,
  scope,
}: {
  connectionId: string;
  scope: Parameters<typeof useVisibleTrackedOperationForResource>[0];
}) => {
  const operation = useVisibleTrackedOperationForResource(scope, {
    id: connectionId,
    role: 'source',
    type: 'connection',
  });

  if (!operation) return null;

  const isActive =
    operation.status === 'queued' || operation.status === 'running';
  const failureMessage =
    operation.failure?.message ||
    (operation.status === 'unavailable'
      ? 'The operation is no longer available. Refresh the connection before trying again.'
      : 'The background update did not complete. Try the action again.');

  return (
    <Alert
      aria-label={
        isActive
          ? 'Connection operation in progress'
          : 'Connection operation failed'
      }
      color={isActive ? 'yellow' : 'red'}
      mb="md"
      mx="md"
      role="alert"
      title={
        isActive ? 'Connection update in progress' : 'Connection update failed'
      }
    >
      <Text size="sm">
        {isActive ? getActiveMessage(operation.operation.type) : failureMessage}
      </Text>
    </Alert>
  );
};
